import React, { useState, useRef, useEffect, useCallback } from 'react';
import { IonIcon } from '@ionic/react';
import { searchOutline, closeOutline } from 'ionicons/icons';
import './ExpandableSearch.css';

const ExpandableSearch = ({ placeholder = 'Search...', value = '', onChange, debounce = 300, onSubmit }) => {
  const [expanded, setExpanded] = useState(false);
  const [localValue, setLocalValue] = useState(value);
  const inputRef = useRef(null);
  const containerRef = useRef(null);
  const debounceRef = useRef(null);

  useEffect(() => {
    setLocalValue(value);
  }, [value]);

  useEffect(() => {
    if (expanded && inputRef.current) {
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [expanded]);

  useEffect(() => {
    if (!expanded) return;
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        if (!localValue) {
          setExpanded(false);
        }
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [expanded, localValue]);

  const handleChange = useCallback((e) => {
    const val = e.target.value;
    setLocalValue(val);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      onChange && onChange(val);
    }, debounce);
  }, [onChange, debounce]);

  const handleClear = () => {
    setLocalValue('');
    onChange && onChange('');
    setExpanded(false);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && onSubmit) {
      onSubmit(localValue);
    }
    if (e.key === 'Escape') {
      if (!localValue) {
        setExpanded(false);
      } else {
        handleClear();
      }
    }
  };

  return (
    <div className={`expandable-search ${expanded ? 'expanded' : ''}`} ref={containerRef}>
      <button
        className="expandable-search-icon"
        onClick={() => setExpanded(true)}
        title="Search"
        type="button"
      >
        <IonIcon icon={searchOutline} style={{ fontSize: 18 }} />
      </button>
      <div className="expandable-search-bar">
        <input
          ref={inputRef}
          type="text"
          placeholder={placeholder}
          value={localValue}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          tabIndex={expanded ? 0 : -1}
        />
        {localValue && (
          <button className="expandable-search-close" onClick={handleClear} type="button" title="Clear">
            <IonIcon icon={closeOutline} />
          </button>
        )}
      </div>
    </div>
  );
};

export default ExpandableSearch;
